import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Copy, Eye, Table2 } from "lucide-react";
import type { ExpectedDebitsTable } from "../types";
import { getApi } from "../api";
import { usePageSetup } from "../hooks/usePageSetup";
import { useShortcuts } from "../hooks/useShortcuts";
import { useAsyncResource } from "../hooks/useAsyncResource";
import { useCatalogStore } from "../store/catalogStore";
import { confirmAction, notify } from "../store/uiStore";
import { reportError } from "../services/eventBus";
import { trackEvent } from "../telemetry/appInsights";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/Button";
import { Alert } from "../components/Alert";
import { DataGrid, type GridColumn } from "../components/DataGrid";
import { IconButton } from "../components/IconButton";
import { IhqTypePill } from "../components/Pills";
import { EmptyState, ErrorState } from "../components/ScreenStates";
import { ToggleInput } from "../components/fields/ToggleInput";
import { ExpectedDebitsTableDrawer } from "../components/actuarial/ExpectedDebitsTableDrawer";
import { formatDate } from "../utils/format";

const DEFAULT_SORT = { key: "createdUtc", direction: "desc" } as const;

export function ExpectedDebitTablesPage() {
  usePageSetup("Expected Debit Tables", null);
  const navigate = useNavigate();
  const setExpectedTables = useCatalogStore((state) => state.setExpectedTables);
  const tables = useAsyncResource(() => getApi().listExpectedDebitTables(), "tables", "ExpectedDebitTablesPage.load");
  const [viewing, setViewing] = useState<ExpectedDebitsTable | null>(null);
  const [activatingId, setActivatingId] = useState<string | null>(null);
  const handleAdd = useCallback(() => navigate("/uw/actuarial/expected-debits/new"), [navigate]);
  const shortcuts = useMemo(() => ({ add: handleAdd }), [handleAdd]);
  useShortcuts(shortcuts);

  const handleActivate = useCallback(
    async (table: ExpectedDebitsTable) => {
      const current = tables.data?.find((candidate) => candidate.ihqType === table.ihqType && candidate.isActive);
      const confirmed = await confirmAction({
        title: `Activate ${table.name}`,
        message: current ? `${current.name} is the active ${table.ihqType} table today. Activating ${table.name} deactivates it and changes RMM on every ${table.ihqType}-form group.` : `Make ${table.name} the active ${table.ihqType} table?`,
        confirmLabel: "Activate",
        variant: "primary",
      });
      if (!confirmed) return;
      setActivatingId(table.id);
      try {
        const updated = await getApi().activateExpectedDebitTable(table.id);
        tables.setData(() => updated);
        setExpectedTables(updated);
        trackEvent("ExpectedDebitsTableActivated", { tableId: table.id, ihqType: table.ihqType });
        notify(`${table.name} is now the active ${table.ihqType} table.`, "success");
      } catch (error) {
        reportError(error, "ExpectedDebitTablesPage.activate");
      } finally {
        setActivatingId(null);
      }
    },
    [tables, setExpectedTables],
  );

  const columns = useMemo<GridColumn<ExpectedDebitsTable>[]>(
    () => [
      { key: "name", header: "Table Name", sortValue: (table) => table.name, render: (table) => <span className="font-medium text-gray-800">{table.name}</span> },
      { key: "ihqType", header: "Form Type", sortValue: (table) => table.ihqType, render: (table) => <IhqTypePill ihqType={table.ihqType} /> },
      {
        key: "active",
        header: "Active",
        align: "center",
        sortValue: (table) => (table.isActive ? 1 : 0),
        render: (table) => <ToggleInput id={`active-${table.id}`} label={`Active: ${table.name}`} hideLabel checked={table.isActive} disabled={table.isActive || activatingId !== null} title={table.isActive ? "Activate another table of this form type to replace it" : undefined} onChange={() => void handleActivate(table)} />,
      },
      { key: "notes", header: "Notes", render: (table) => <span className="text-gray-600">{table.notes ?? ""}</span> },
      { key: "createdUtc", header: "Created Date", sortValue: (table) => table.createdUtc, render: (table) => formatDate(table.createdUtc) },
      { key: "createdBy", header: "Created By", sortValue: (table) => table.createdByName, render: (table) => table.createdByName },
    ],
    [activatingId, handleActivate],
  );

  const { state } = tables;

  return (
    <>
      <PageHeader
        title="Expected Debit Tables"
        subtitle="Exactly one active table per form type. Tables are immutable once saved; copy one to change it."
        actions={
          <Button onClick={handleAdd} icon={Table2}>
            New Table
          </Button>
        }
      />
      <Alert type="info" message="The mockup ships with PLACEHOLDER tables. Replace them with the actuarial tables before production." className="mb-4" />
      {state.kind === "error" && <ErrorState message={state.message} onRetry={tables.reload} />}
      {state.kind !== "error" && (
        <DataGrid
          caption="Expected debits tables"
          columns={columns}
          rows={state.data ?? []}
          rowKey={(table) => table.id}
          isLoading={state.kind === "loading"}
          defaultSort={DEFAULT_SORT}
          actionsHeader="Table actions"
          renderActions={(table) => (
            <span className="inline-flex">
              <IconButton icon={Eye} label={`View ${table.name}`} onClick={() => setViewing(table)} />
              <IconButton icon={Copy} label={`Copy ${table.name}`} onClick={() => navigate(`/uw/actuarial/expected-debits/new?copyOf=${encodeURIComponent(table.id)}`)} />
            </span>
          )}
          emptyState={<EmptyState icon={Table2} message="No expected debit tables yet. Create one to drive RMM." action={<Button onClick={handleAdd}>New Table</Button>} />}
        />
      )}
      {viewing && <ExpectedDebitsTableDrawer table={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}
